import { useCallback, useMemo, useState } from "react"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectLineTemplate,
    selectRulerTemplate,
    updateLineTemplate,
} from "plugins/Toolbox/tool-registry"
import { deleteLine, updateLine } from "features/pubsub/entityActions/cubes/line"
import { createRuler } from "features/pubsub/entityActions/cubes/ruler"
import { omit } from "nodash"
import { ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import {
    selectLineById,
    selectLineColorById,
    selectLineFillById,
    selectLineOutlineById,
    selectLineStrokeById,
    selectLineWidthById,
} from "features/entity-caching/lines/selectors"
import {
    ColorSwatchCategory,
    GeometryType,
    hexColorSwatches,
    LineStroke,
    lineStrokeNameByLineStroke,
    LineWidth,
    lineWidthNameByLineWidth,
} from "@nova/constants"
import { InspectorPanelKeyValueDropdown } from "../../InspectorPanelKeyValueDropdown"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { ColorPalette } from "../../icons/TODO-put-in-package/ColorPalette"
import {
    OptimisticLocalStateParams,
    useDefinedOptimisticLocalState,
} from "../../useOptimisticLocalState"
import { patterns, thicknesses } from "../constants"
import { LinePatternPicker } from "../LinePatternPicker"
import { LineThicknessPicker } from "../LineThicknessPicker"
import { InspectorPanelSettingsFields } from "../../InspectorPanelSettingsFields"
import { Selector } from "react-redux"
import { RootState } from "integrations/redux"
import { moveLineToEditable } from "plugins/Toolbox/Line/LineSlice"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"

const polygonOutlineColorSwatches = hexColorSwatches[ColorSwatchCategory.PolygonOutline].map(
    (hex) => encodeColorToNumber(hex)
)
const polygonFillColorSwatches = hexColorSwatches[ColorSwatchCategory.PolygonFill].map((hex) =>
    encodeColorToNumber(hex)
)
const lineColorSwatches = hexColorSwatches[ColorSwatchCategory.LineColor].map((hex) =>
    encodeColorToNumber(hex)
)

function useInspectorPanelLineLayer({
    outlineParams,
    fillParams,
    colorParams,
    strokeParams,
    widthParams,
}: {
    outlineParams: OptimisticLocalStateParams<number>
    fillParams: OptimisticLocalStateParams<number>
    colorParams: OptimisticLocalStateParams<number>
    strokeParams: OptimisticLocalStateParams<LineStroke>
    widthParams: OptimisticLocalStateParams<LineWidth>
}) {
    const [openDropdownId, setOpenDropdownId] = useState<string>()
    const [outline, setOutline] = useDefinedOptimisticLocalState(outlineParams)
    const [fill, setFill] = useDefinedOptimisticLocalState(fillParams)
    const [color, setColor] = useDefinedOptimisticLocalState(colorParams)
    const [stroke, setStroke] = useDefinedOptimisticLocalState(strokeParams)
    const [width, setWidth] = useDefinedOptimisticLocalState(widthParams)
    return {
        openDropdownId,
        setOpenDropdownId,
        outline,
        setOutline,
        fill,
        setFill,
        color,
        setColor,
        stroke,
        setStroke,
        width,
        setWidth,
    }
}

export const InspectorPanelLineLayer = ({
    id,
    shouldShowTopBorder = true,
}: {
    id?: string
    shouldShowTopBorder?: boolean
}) => {
    const dispatch = useAppDispatch()
    const projectId = useAppSelector(selectActivatedFireId)
    const { isEditor } = useWorkspacePermissions(projectId)
    const line = useAppSelector(selectLineById(id || ""))
    const lineTemplate = useAppSelector(selectLineTemplate)

    const hookParams: Parameters<typeof useInspectorPanelLineLayer>[0] = useMemo(
        () =>
            !!id
                ? {
                      outlineParams: {
                          selector: selectLineOutlineById(id) as Selector<RootState, number>,
                          updateFn: (outline) => {
                              dispatch(updateLine({ id, updates: { outline } }))
                              dispatch(updateLineTemplate({ outline, color: outline }))
                          },
                          defaultValue: line.outline || polygonOutlineColorSwatches[0],
                      },
                      fillParams: {
                          selector: selectLineFillById(id) as Selector<RootState, number>,
                          updateFn: (fill) => {
                              dispatch(updateLine({ id, updates: { fill } }))
                              dispatch(updateLineTemplate({ fill }))
                          },
                          defaultValue: line.fill || polygonFillColorSwatches[0],
                      },
                      colorParams: {
                          selector: selectLineColorById(id) as Selector<RootState, number>,
                          updateFn: (color) => {
                              dispatch(updateLine({ id, updates: { color } }))
                              dispatch(
                                  updateLineTemplate({
                                      color,
                                      outline: color,
                                      fill: (((color >>> 0) & 0xffffff00) | 0x00000040) >>> 0,
                                  })
                              )
                          },
                          defaultValue: line.color || lineColorSwatches[0],
                      },
                      strokeParams: {
                          selector: selectLineStrokeById(id) as Selector<RootState, LineStroke>,
                          updateFn: (stroke) => {
                              dispatch(updateLine({ id, updates: { stroke } }))
                              dispatch(updateLineTemplate({ stroke }))
                          },
                          defaultValue: line.stroke || LineStroke.normal,
                      },
                      widthParams: {
                          selector: selectLineWidthById(id) as Selector<RootState, LineWidth>,
                          updateFn: (width) => {
                              dispatch(updateLine({ id, updates: { width } }))
                              dispatch(updateLineTemplate({ width }))
                          },
                          defaultValue: line.width || LineWidth.normal,
                      },
                  }
                : {
                      outlineParams: {
                          selector: () => lineTemplate.outline,
                          updateFn: (outline) => {
                              dispatch(updateLineTemplate({ outline, color: outline }))
                          },
                          defaultValue: lineTemplate.outline,
                      },
                      fillParams: {
                          selector: () => lineTemplate.fill,
                          updateFn: (fill) => {
                              dispatch(updateLineTemplate({ fill }))
                          },
                          defaultValue: lineTemplate.fill,
                      },
                      colorParams: {
                          selector: () => lineTemplate.color,
                          updateFn: (color) => {
                              dispatch(
                                  updateLineTemplate({
                                      color,
                                      outline: color,
                                      fill: (((color >>> 0) & 0xffffff00) | 0x00000040) >>> 0,
                                  })
                              )
                          },
                          defaultValue: lineTemplate.color,
                      },
                      strokeParams: {
                          selector: () => lineTemplate.stroke,
                          updateFn: (stroke) => {
                              dispatch(updateLineTemplate({ stroke }))
                          },
                          defaultValue: lineTemplate.stroke,
                      },
                      widthParams: {
                          selector: () => lineTemplate.width,
                          updateFn: (width) => {
                              dispatch(updateLineTemplate({ width }))
                          },
                          defaultValue: lineTemplate.width,
                      },
                  },
        [
            dispatch,
            id,
            line?.color,
            line?.fill,
            line?.outline,
            line?.stroke,
            line?.width,
            lineTemplate.color,
            lineTemplate.fill,
            lineTemplate.outline,
            lineTemplate.stroke,
            lineTemplate.width,
        ]
    )

    const {
        openDropdownId,
        setOpenDropdownId,
        outline,
        setOutline,
        fill,
        setFill,
        color,
        setColor,
        stroke,
        setStroke,
        width,
        setWidth,
    } = useInspectorPanelLineLayer(hookParams)

    const rulerTemplate = useAppSelector(selectRulerTemplate)
    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const workspaceAreaUnit = useAppSelector(selectWorkspaceAreaUnit)

    const convertToRuler = useCallback(() => {
        if (!id) return
        dispatch(deleteLine({ id }))

        const distanceUnit = rulerTemplate.distanceUnit || workspaceDistanceUnit
        const areaUnit = rulerTemplate.areaUnit || workspaceAreaUnit

        dispatch(
            createRuler({
                data: {
                    ...omit(line, ["id", "index"]),
                    distanceUnit,
                    areaUnit,
                },
                context: { name: line.name, index: line.index },
            })
        )
    }, [
        dispatch,
        id,
        line,
        rulerTemplate.areaUnit,
        rulerTemplate.distanceUnit,
        workspaceAreaUnit,
        workspaceDistanceUnit,
    ])

    const geometryType = line?.geometryType || GeometryType.Line

    return (
        <>
            <InspectorPanelSettingsFields shouldShowTopBorder={shouldShowTopBorder}>
                {(geometryType === GeometryType.Polygon || !id) && (
                    <>
                        {/* Outline (polygons only, not lines, unless user is choosing colors for a mocked polygon) */}
                        <InspectorPanelKeyValueDropdown
                            id="outline"
                            isOpen={openDropdownId === "outline"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Outline"
                            Icon={Icons.BucketColor}
                            iconClasses="text-gray-0"
                            iconAccentColor={decodeNumberToColor(outline)}
                        >
                            <ColorPalette
                                swatches={polygonOutlineColorSwatches}
                                selectedColor={outline}
                                setSelectedColor={setOutline}
                                setOpenDropdownId={setOpenDropdownId}
                            />
                        </InspectorPanelKeyValueDropdown>

                        {/* Fill (polygons only, not lines, unless user is choosing colors for a mocked polygon) */}
                        <InspectorPanelKeyValueDropdown
                            id="fill"
                            isOpen={openDropdownId === "fill"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Fill"
                            Icon={Icons.BucketColor}
                            iconClasses="text-gray-0"
                            iconAccentColor={decodeNumberToColor(fill)}
                        >
                            <ColorPalette
                                swatches={polygonFillColorSwatches}
                                selectedColor={fill}
                                setSelectedColor={setFill}
                                setOpenDropdownId={setOpenDropdownId}
                                includeTransparentOption
                            />
                        </InspectorPanelKeyValueDropdown>
                    </>
                )}

                {/* Color (lines only, not polygons) */}
                {geometryType === GeometryType.Line && (
                    <>
                        {/* Color */}
                        {!!id && (
                            <InspectorPanelKeyValueDropdown
                                id="color"
                                isOpen={openDropdownId === "color"}
                                setOpenDropdownId={setOpenDropdownId}
                                keyTitle="Color"
                                Icon={Icons.BucketColor}
                                iconClasses="text-gray-0"
                                iconAccentColor={decodeNumberToColor(color)}
                            >
                                <ColorPalette
                                    swatches={lineColorSwatches}
                                    selectedColor={color}
                                    setSelectedColor={setColor}
                                    setOpenDropdownId={setOpenDropdownId}
                                />
                            </InspectorPanelKeyValueDropdown>
                        )}

                        {/* Pattern */}
                        <InspectorPanelKeyValueDropdown
                            id="pattern"
                            isOpen={openDropdownId === "pattern"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Pattern"
                            Icon={patterns[stroke]}
                            dropdownTitle={lineStrokeNameByLineStroke[stroke]}
                            iconClasses="text-gray-0"
                        >
                            <LinePatternPicker
                                selectedPattern={stroke}
                                setSelectedPattern={setStroke}
                                setOpenDropdownId={setOpenDropdownId}
                            />
                        </InspectorPanelKeyValueDropdown>

                        {/* Thickness */}
                        <InspectorPanelKeyValueDropdown
                            id="thickness"
                            isOpen={openDropdownId === "thickness"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Thickness"
                            Icon={thicknesses[width]}
                            dropdownTitle={lineWidthNameByLineWidth[width]}
                            iconClasses="text-gray-0"
                            align="left"
                        >
                            <LineThicknessPicker
                                selectedThickness={width}
                                setSelectedThickness={setWidth}
                                setOpenDropdownId={setOpenDropdownId}
                            />
                        </InspectorPanelKeyValueDropdown>
                    </>
                )}

                {/* Convert to Ruler Button */}
                {isEditor && !!id && (
                    <>
                        <ButtonTertiary
                            LeftIcon={Icons.PencilSquare}
                            title="Update Shape"
                            className="mt-2"
                            onClick={() => dispatch(moveLineToEditable(line))}
                        />

                        <div className="border-t border-gray-700 mt-2">
                            <ButtonSecondary
                                LeftIcon={Icons.DesignServices}
                                title="Convert to Ruler"
                                className="mt-2 w-full"
                                onClick={convertToRuler}
                            />
                        </div>
                    </>
                )}
            </InspectorPanelSettingsFields>
        </>
    )
}
